import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useMatch, useNavigate, useParams } from "react-router-dom";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import queryString from "query-string";
import useWorkbenchBasePath from "rwb/pages/project/hooks/useWorkbenchBasePath";
import { SWRResponse, useSWRConfig } from "swr";
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from "use-query-params";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useUserPermission from "~/hooks/useUserPermission";
import { apiClient, isApiError } from "~/utils/apiClient";
import { extractS3Key, isS3Link } from "~/utils/s3";

const { VITE_RWB_API } = import.meta.env;
const baseUrl = `${VITE_RWB_API}/file-browser`;

export const ORGANIZATION_REGEX = /^(raw|standardized|enriched|study_management)/;
export const PROJECTS_REGEX = /^research\/projects\/(?<projectId>\d+)/;
const SCRATCH_REGEX = /^scratch\/users\/([^/]+@[^/]+)/;

export const BASE_DIRECTORIES: Record<string, { title: string; subtitle?: string }> = {
  [BaseDirectory.ORG]: {
    title: "Organization",
    subtitle: "Visible to others",
  },
  [`${BaseDirectory.ORG}/${BaseDirectory.RAW}`]: {
    title: "Raw",
  },
  [`${BaseDirectory.ORG}/${BaseDirectory.STANDARD}`]: {
    title: "Standardized",
  },
  [`${BaseDirectory.ORG}/${BaseDirectory.ENRICHED}`]: {
    title: "Enriched",
  },
  [`${BaseDirectory.ORG}/${BaseDirectory.STUDY_MANAGEMENT}`]: {
    title: "Study Management",
  },
  [BaseDirectory.PROJECTS]: {
    title: "Workbenches",
    subtitle: "Visible to others",
  },
  [BaseDirectory.PROJECT]: {
    title: "Workbench",
    subtitle: "Visible to others",
  },
  [BaseDirectory.PERSONAL]: {
    title: "Personal",
    subtitle: "Only visible to you",
  },
  [BaseDirectory.CURRENT_DATASET]: {
    title: "Dataset Files",
  },
};

export function getBaseDirectorySubtitle(
  resolvedPath: string,
  datasetId?: string
): string | undefined {
  if (resolvedPath === BaseDirectory.CURRENT_DATASET) {
    return datasetId ? `datasets/${datasetId}` : undefined;
  }
  return BASE_DIRECTORIES[resolvedPath]?.subtitle;
}

/**
 * Safely URL encode each segment in an object's key
 */
export const encodeObjectKey = (key: string) => {
  return key
    .split("/")
    .map((segment) => {
      try {
        return encodeURIComponent(segment);
      } catch (e) {
        return segment;
      }
    })
    .join("/");
};

/**
 * This function takes a full S3 URI (with bucket prefix) and converts it to a
 * path that can be used for navigation in the file browser.
 *
 * @param {string} s3Uri - The full S3 URI (e.g., "s3://bucket-name/research/projects/123/folder/")
 * @param {string} projectId - Optional project ID for context-specific routing. When provided,
 *                            the link will use the workbench route (/workbenches/:projectId/files)
 * @returns {string} The router path for the file browser, or empty string if invalid
 */
export const convertS3UriToFileBrowserLink = (
  s3Uri: string,
  projectId?: string,
  datasetId?: string
): string => {
  if (!s3Uri) {
    return "";
  }

  if (!isS3Link(s3Uri)) {
    return "";
  }

  const s3Key = extractS3Key(s3Uri);

  const routerPath = resolveRouterPath(s3Key, !projectId, datasetId);

  return `/workbenches/${projectId}/files/${routerPath}`;
};

/**
 * Remaps original S3 object key prefixes for frontend display and routing.
 *
 * This function transforms S3 paths into more user-friendly paths for the frontend.
 * It handles special cases like organization directories, project directories, and
 * personal directories.
 *
 * @param {string} key - The original S3 object key
 * @param {boolean} multiProject - Whether to display in multi-project context (true) or
 *                                single project context (false)
 * @returns {string} The transformed path for frontend routing
 *
 * @example
 * // Transform an organization path
 * const path = resolveRouterPath("raw/data"); // Returns "organization/raw/data"
 *
 * // Transform a project path in multi-project context
 * const path = resolveRouterPath("research/projects/123", true); // Returns "projects/123"
 *
 * // Transform a project path in single project context
 * const path = resolveRouterPath("research/projects/123", false); // Returns "project"
 *
 * // Transform a personal path
 * const path = resolveRouterPath("scratch/users/user@example.com"); // Returns "personal"
 */
export const resolveRouterPath = (key: string, multiProject = true, datasetId?: string) => {
  // If we're in a dataset context and the key starts with datasets/:datasetId, map to current-dataset
  if (datasetId) {
    const datasetPrefix = `datasets/${datasetId}`;
    if (key === datasetPrefix || key.startsWith(datasetPrefix + "/")) {
      const remainder = key.slice(datasetPrefix.length);
      return `${BaseDirectory.CURRENT_DATASET}${remainder}`.replace(/\/$/, "");
    }
  }

  return (
    key
      // Prepend raw/standardized/enriched/study_management with organization virtual directory
      .replace(ORGANIZATION_REGEX, `${BaseDirectory.ORG}/$1`)
      // /research/projects/:id -> files/projects/:id OR /projects/:projectId/files/project
      // Depending on context of file browser (top level or inside a singular project)
      .replace(
        PROJECTS_REGEX,
        multiProject ? `${BaseDirectory.PROJECTS}/$1` : BaseDirectory.PROJECT
      )
      //  /scratch/users/:id -> personal
      .replace(SCRATCH_REGEX, BaseDirectory.PERSONAL)
      // Remove trailing slash
      .replace(/\/$/, "")
  );
};

/**
 * Converts frontend routing paths back to their original S3 object keys.
 *
 * This function is the inverse of `resolveRouterPath`. It transforms user-friendly
 * frontend paths back into the actual S3 paths used in the backend.
 *
 * @param {string} path - The frontend path to convert
 * @param {string} userId - The current user's ID (email), used for personal directory resolution
 * @param {string} projectId - The current project ID, used for project directory resolution
 * @returns {string} The original S3 object key
 *
 * @example
 * // Convert an organization path
 * const key = resolveObjectKey("organization/raw", "user@example.com");
 * // Returns "raw"
 *
 * // Convert a project path in a specific project context
 * const key = resolveObjectKey("project", "user@example.com", "123");
 * // Returns "research/projects/123"
 *
 * // Convert a project path in multi-project context
 * const key = resolveObjectKey("projects/123", "user@example.com");
 * // Returns "research/projects/123"
 *
 * // Convert a personal path
 * const key = resolveObjectKey("personal", "user@example.com");
 * // Returns "scratch/users/user@example.com"
 */
export const resolveObjectKey = (path: string, userId = "", projectId = "", datasetId = "") => {
  const [projectRegex, projectReplacement] = projectId
    ? [new RegExp(`^${BaseDirectory.PROJECT}`), `research/projects/${projectId}`]
    : [new RegExp(`^${BaseDirectory.PROJECTS}/(.+)`), "research/projects/$1"];

  const key = path
    // Only strip organization if within a subdirectory
    .replace(new RegExp(`^${BaseDirectory.ORG}/(.+)`), "$1")
    .replace(projectRegex, projectReplacement)
    .replace(new RegExp(`^${BaseDirectory.PERSONAL}`), `scratch/users/${userId}`);

  // Map current-dataset back to datasets/:datasetId only when datasetId is known
  return datasetId
    ? key.replace(new RegExp(`^${BaseDirectory.CURRENT_DATASET}(/?.*)$`), `datasets/${datasetId}$1`)
    : key;
};

/**
 * A hook that retrieves the current S3 object key based on the current route.
 *
 * This hook either gets the object key from the files response or resolves it from
 * the shortened router URL as a fallback if the key is unknown or missing. It's useful
 * for getting the actual S3 path for the current location in the file browser.
 *
 * @returns {string} The current S3 object key
 *
 * @example
 * // Get the current S3 object key
 * const currentKey = useCurrentKey();
 *
 * // Use it to check permissions
 * const canWrite = useFileAccess(currentKey, "write");
 *
 * // Use it to construct a new path
 * const newFolderKey = `${currentKey}new-folder/`;
 */
export const useCurrentKey = () => {
  const user = useUser();
  const filePath = useCurrentPath();
  const { projectId, datasetId } = useParams();
  const { data } = useFiles();

  if (data?.current_file?.key) {
    return data?.current_file?.key;
  }

  // Handle scenario where current file is null due to deletion or direct navigation to unknown path
  let fileKey = resolveObjectKey(filePath, user?.email, projectId, datasetId);

  // Append trailing slash if necessary
  if (!filePath.endsWith("/")) {
    fileKey += "/";
  }

  return fileKey;
};

/**
 * A hook that retrieves the current path from the URL.
 *
 * This hook extracts the file path portion from the current URL, using the base path
 * determined by `useBasePath`. It removes any trailing slashes from the path.
 *
 * @returns {string} The current file path from the URL
 *
 * @example
 * // If the URL is /files/organization/raw/data
 * const path = useCurrentPath(); // Returns "organization/raw/data"
 *
 * // If the URL is /workbenches/123/files/folder
 * const path = useCurrentPath(); // Returns "folder"
 */
export const useCurrentPath = (params?: { basePath: string; currentPath: string }) => {
  const urlBasePath = useBasePath();
  // Unfortunately react-router v6 does not allow regex in matches anymore
  // See: https://github.com/remix-run/react-router/discussions/8132
  const routerMatch = useMatch(`${urlBasePath}/*`);

  if (!params) {
    const { "*": filePath = "" } = routerMatch?.params ?? {};

    return filePath.replace(/\/$/, "");
  }

  const pathMatch = matchPath(`${params.basePath}/*`, params.currentPath);
  const { "*": filePath = "" } = pathMatch?.params ?? {};
  return filePath.replace(/\/$/, "");
};

/**
 * A hook that provides file search functionality.
 *
 * This hook handles searching for files based on the current query parameters and location.
 * It automatically scopes the search to the current directory unless the 'root' parameter
 * is set to true, in which case it searches from the root.
 *
 * The hook:
 * - Extracts search parameters from the URL
 * - Determines the search scope based on the current directory
 * - Constructs the search query
 * - Fetches search results from the API
 * - Handles loading states
 */
export const useFilesSearch = () => {
  const { projectId } = useParams();
  const [{ q: query, root }] = useQueryParams({
    q: StringParam,
    root: BooleanParam,
  });
  const { data } = useFiles();
  // Only search once we know if the user is inside a valid directory or not
  // in order to initially scope search to that prefix
  const shouldSearch = Boolean(query && data?.current_file);
  // Strip off last segment from key (to use parent) if searching inside a file preview
  const prefix =
    data?.current_file?.type === FileType.FILE
      ? data?.current_file?.key.replace(/([^/]+)$/, "")
      : data?.current_file?.key;

  const qs = queryString.stringify({
    query,
    project_id: projectId,
    prefix: !root ? prefix : undefined,
  });

  const resp = useFetcher<{ files: FileInfo[]; truncated: boolean }>(
    shouldSearch ? `${baseUrl}/search?${qs}` : null,
    { keepPreviousData: true }
  );

  // Show loading when file browsing list is loading (and search request has not fired yet)
  return { ...resp, isLoading: !!query && (!data || resp.isLoading) };
};

export interface FilesListResponse {
  children: FileInfo[] | null;
  current_file: FileInfo | null;
  next_continuation_token: string | null;
  has_more: boolean;
}

/**
 * Creates a virtual directory structure for top-level navigation.
 *
 * Used to build the root, organization, and projects prefixes that don't exist in S3.
 *
 * @param directories - Directory keys to include as children
 * @param currentPrefix - Current directory prefix (empty string for root)
 * @returns A FilesListResponse with the current directory and its children
 */
const buildBaseDirResponse = (directories: string[], currentPrefix: string) => {
  return {
    current_file: {
      key: currentPrefix ? `${currentPrefix}/` : currentPrefix,
      type: FileType.DIRECTORY,
    },
    children: directories.map((directory) => ({
      key: `${directory}/`,
      type: FileType.DIRECTORY,
    })),
  };
};

/**
 * Returns null while loading, true if the `datasets/:datasetId` S3 prefix exists, false otherwise.
 * Pass undefined to skip the check entirely (returns false immediately).
 * Uses the same SWR key as the file-browser endpoint, so navigating into the dataset directory
 * warms the cache and subsequent root-level checks are instant.
 */
const useDatasetDirectoryExists = (datasetId: string | undefined): boolean | null => {
  const { data, error, isLoading } = useFetcher<FilesListResponse>(
    datasetId ? `${baseUrl}/${encodeObjectKey(`datasets/${datasetId}`)}` : null,
    {
      // RWB will return 403 if the directory does not exist (or the user doesn't have access).
      // Don't retry for any 4xx response. Do retry for 5xx; these could be transient.
      // Retries use exponential backoff. Max out at 5 attempts.
      shouldRetryOnError: (err) => !(isApiError(err) && err.status >= 400 && err.status < 500),
      errorRetryCount: 5,
    }
  );
  if (error) return false; // RWB error: directory does not exist
  if (isLoading) return null; // still working
  return !!data?.current_file; // RWB returned a result; use that result
};

/**
 * Used to build directories that don't truly exist in S3, e.g. root, organization, and projects
 * Project directories are determined using the project access response as opposed to file-browsing
 * endpoint due to IAM permission limitations with listing /research/projects
 * @param prefix - Pointer to current place in directory
 * @param multiProject - Used to determine if "Project" or "Projects" should be presented (dependent on context)
 */
const useVirtualDirectory = (
  prefix: string,
  multiProject: boolean
): SWRResponse<FilesListResponse> | null => {
  const { projectId, datasetId } = useParams();
  const projectsAccessList = useProjectsAccessList();
  const canViewPersonalFiles = useUserPermission("view:file-browser-personal-folder");
  const rawAccess = useFileAccess(BaseDirectory.RAW, "read");
  const standardAccess = useFileAccess(BaseDirectory.STANDARD, "read");
  const enrichAccess = useFileAccess(BaseDirectory.ENRICHED, "read");
  const studyManageAccess = useFileAccess(BaseDirectory.STUDY_MANAGEMENT, "read");
  // Only probe at root — pass undefined elsewhere to skip the request entirely.
  const datasetDirExists = useDatasetDirectoryExists(!prefix ? datasetId : undefined);
  const loading =
    projectsAccessList.isLoading ||
    [rawAccess, standardAccess, enrichAccess, datasetDirExists].some((access) => access === null);
  // Prefix is root (empty string), organization, or projects directory
  const data = useMemo(() => {
    const directories = [];

    if (!prefix) {
      if (datasetDirExists) {
        directories.push(`datasets/${datasetId}`);
      }
      // Show Organization if user has access to any of its subdirectories
      if (rawAccess || standardAccess || enrichAccess || studyManageAccess) {
        directories.push(BaseDirectory.ORG);
      }
      if (!multiProject && projectId) {
        // Viewing root from within a singular project context
        directories.push(BaseDirectory.PROJECT);
      } else if (projectsAccessList.data?.length) {
        directories.push(BaseDirectory.PROJECTS);
      }
      if (canViewPersonalFiles) {
        directories.push(BaseDirectory.PERSONAL);
      }
    }

    if (prefix === BaseDirectory.ORG) {
      if (rawAccess) {
        directories.push(BaseDirectory.RAW);
      }
      if (standardAccess) {
        directories.push(BaseDirectory.STANDARD);
      }
      if (enrichAccess) {
        directories.push(BaseDirectory.ENRICHED);
      }
      if (studyManageAccess) {
        directories.push(BaseDirectory.STUDY_MANAGEMENT);
      }
    }

    if (prefix === BaseDirectory.PROJECTS && projectsAccessList.data) {
      directories.push(
        ...projectsAccessList.data.map(
          ({ project_id }) => `${BaseDirectory.PROJECTS}/${project_id}`
        )
      );
    }

    if (directories) {
      return buildBaseDirResponse(directories, prefix);
    }
  }, [
    loading,
    prefix,
    rawAccess,
    standardAccess,
    enrichAccess,
    studyManageAccess,
    projectsAccessList.data,
    datasetId,
    datasetDirExists,
    canViewPersonalFiles,
    multiProject,
    projectId,
  ]);

  const isVirtualDirectory = ["", BaseDirectory.ORG, BaseDirectory.PROJECTS].includes(prefix);

  if (!isVirtualDirectory) {
    return null;
  }

  return {
    data,
    isLoading: loading,
    mutate: async () => undefined,
  } as SWRResponse<FilesListResponse>;
};

export function removePathLastSection(path: string | undefined) {
  if (!path || !path.includes("/")) {
    return "";
  }
  return path.slice(0, path.lastIndexOf("/"));
}

export interface UseMemoryNavigationParams {
  /**
   * Base path to use for navigation (overrides the default, which is the
   * current workbench's file root in whichever shell the workbench is mounted)
   */
  basePath?: string;
  /**
   * Starting directory within the base path to initialize navigation at
   * @default workbench
   */
  startingDirectory?: string;
}

/** *
 * This hook provides state and functionality to navigate through files and directories
 * without relying on URL changes. It maintains the current path and allows updating it,
 * while also fetching the relevant file data for the current location.
 *
 * The intended way of navigating is to use the `setCurrentPath` function to update the current path.
 * If you're navigating based on the fileKey generated from the files response, ensure to resolve it
 * to a router-compatible path using `resolveRouterPath` before passing it to `setCurrentPath`.
 * @returns An object containing the base path, current path, a function to set the current path,
 *          the current file key, and the file data for the current location.
 */
export function useMemoryNavigation({
  basePath: externalBasePath,
  startingDirectory,
}: UseMemoryNavigationParams = {}) {
  const user = useUser();
  const { projectId, datasetId } = useParams();
  const workbenchBasePath = useWorkbenchBasePath();
  const basePath = externalBasePath ?? `${workbenchBasePath}/files`;
  const [currentPath, setCurrentPath] = useState(`${basePath}/${startingDirectory ?? "workbench"}`);
  const directoryPath = useCurrentPath({
    basePath,
    currentPath,
  });
  const currentFileKey = resolveObjectKey(directoryPath, user?.email, projectId, datasetId);
  const fileData = useFiles(currentFileKey);

  return { basePath, currentPath, setCurrentPath, currentFileKey, fileData };
}

/**
 * Used to retrieve current state of browsing, either using current router path (default)
 * or a prefix override (e.g. FileTree component which does not update router state).
 * @param prefixOverride - Override default behavior of determining where to list files from based on router state
 * @param multiProject - Used to determine if "Project" or "Projects" should be presented (dependent on context)
 * @returns current directory/file information and direct children (in the case of directories, but not for files)
 */
const useFiles = (prefixOverride?: string, multiProject = false, retryWhenNotReady = false) => {
  const user = useUser();
  const navigate = useNavigate();
  const { mutate: mutateCache } = useSWRConfig();
  const { projectId, datasetId } = useParams();
  const directoryPath = useCurrentPath();
  const [useParentKey] = useQueryParam("preview", BooleanParam);
  const objectKey = resolveObjectKey(directoryPath, user?.email, projectId, datasetId);
  const parentKey = useParentKey ? objectKey.replace(/\/[^/]+$/, "") : null;
  const prefix = prefixOverride ?? parentKey ?? objectKey;
  const virtualDirResp = useVirtualDirectory(prefix, multiProject);
  // A freshly-created directory (e.g. a new dataset's folder) can list as not-yet-
  // materialized before its contents appear, due to S3 lag. When `retryWhenNotReady`
  // is set (only the directory-listing views opt in), these refs drive a bounded
  // auto-refresh of that directory: mutable counters that persist across renders
  // without triggering them, reset whenever the directory (SWR key) changes. Other
  // callers (dialogs, mutate-only, useCurrentKey) must NOT poll — several are mounted
  // even at the root, so polling there would refresh the listing unexpectedly.
  const retryAttempts = useRef(0);
  const retriedPrefix = useRef(prefix);
  if (retriedPrefix.current !== prefix) {
    retriedPrefix.current = prefix;
    retryAttempts.current = 0;
  }
  const filesResp = useFetcher<FilesListResponse>(
    // Avoid requesting if user is at a virtual directory or handling preview share link redirect
    !virtualDirResp ? `${baseUrl}/${encodeObjectKey(prefix)}` : null,
    {
      keepPreviousData: true,
      // Only the directory-listing views opt in. The function form is intentional:
      // SWR reschedules polling by re-running its effect (keyed on refreshInterval
      // identity) after data loads, so a fresh closure each render is what lets the
      // poll bootstrap once the first (not-ready) response arrives.
      ...(retryWhenNotReady
        ? {
            refreshInterval: (data: FilesListResponse | undefined) =>
              getFileListingRetryInterval(data, retryAttempts.current),
            onSuccess: (data: FilesListResponse) => {
              retryAttempts.current = isDirectoryNotReady(data) ? retryAttempts.current + 1 : 0;
            },
          }
        : {}),
    }
  );

  const handlePreviewNavigation = (fileInfo: FileInfo) =>
    navigate("..", {
      relative: "path",
      // replace: true,
      state: { preview: { key: fileInfo.key, size: fileInfo.size } },
    });

  useEffect(() => {
    // Handle consuming `preview` boolean qs param if present in url, find file
    // in browse response and navigate user to parent directory with preview modal opened
    if (useParentKey && !filesResp.isLoading) {
      const previewFile = filesResp.data?.children?.find((file) => file.key === objectKey);
      if (previewFile) {
        handlePreviewNavigation(previewFile);
      }
    }
  }, [useParentKey, objectKey, filesResp]);

  useEffect(() => {
    const pointer = filesResp.data?.current_file;
    // Handle scenario where user goes directly to file preview url without share link qs indicator (?preview=1)
    // We must first check with backend to confirm whether path is a file or not,
    // if it is a file, navigate upwards and re-trigger a new request to get directory contents
    // and display preview with original file pointer from first response
    if (!filesResp.isLoading && pointer?.type === FileType.FILE) {
      handlePreviewNavigation(pointer);
    }
  }, [objectKey, filesResp]);

  // Files.tsx holds two simultaneous SWR subscriptions for the same directory:
  // the non-paginated key (useCurrentKey → useFiles) and the paginated key
  // (useFilesInfinite, `?limit=N`). A bound mutate only revalidates its own key.
  // The cache-scoped mutate with a predicate iterates the whole cache and
  // revalidates every entry whose key starts with this directory's encoded
  // prefix, so both responses are refreshed in a single call. It comes from
  // useSWRConfig so it reaches the cache provider the page is rendered under.
  const mutate = useCallback(async () => {
    await mutateCache(
      (key: unknown) =>
        typeof key === "string" && key.startsWith(`${baseUrl}/${encodeObjectKey(prefix)}`)
    );
  }, [mutateCache, prefix]);

  return virtualDirResp || { ...filesResp, mutate };
};

export default useFiles;

/**
 * A freshly-created S3 directory (e.g. a new dataset's folder) can list as empty for a
 * moment before its contents become visible. The backend signals this "not yet
 * materialized" state with a null current_file and no children. A directory that
 * legitimately exists — even an empty folder created via create-folder — always carries
 * a directory-marker object, so current_file is populated and this returns false.
 */
export const isDirectoryNotReady = (data?: FilesListResponse): boolean =>
  !!data && data.current_file == null && !data.children?.length;

/** Poll interval used to re-list a directory that is not yet materialized in S3. */
export const FILE_LISTING_RETRY_INTERVAL_MS = 1500;

/** Upper bound on not-ready re-list attempts, so a nonexistent path cannot poll forever. */
export const MAX_FILE_LISTING_RETRIES = 6;

/**
 * SWR refreshInterval computed from the latest listing and the number of not-ready
 * responses seen so far. Returns the poll interval while the directory is not yet
 * materialized and the attempt budget is not exhausted, otherwise 0 (stop polling).
 */
export const getFileListingRetryInterval = (
  data: FilesListResponse | undefined,
  attempts: number
): number =>
  isDirectoryNotReady(data) && attempts < MAX_FILE_LISTING_RETRIES
    ? FILE_LISTING_RETRY_INTERVAL_MS
    : 0;

/** Maximum number of S3 objects to fetch per page in the main directory view. */
const PAGE_LIMIT = 200;

export interface UseFilesInfiniteResult {
  data: FilesListResponse | undefined;
  allChildren: FileInfo[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * Paginated variant of `useFiles` for the main directory view.
 *
 * Fetches the first page of S3 directory contents with a limit of 200 items, then
 * exposes a `loadMore` function to append subsequent pages as the user scrolls.
 * Virtual directories (root, organization, projects) are not paginated since they
 * are always small.
 *
 * `retryWhenNotReady` mirrors `useFiles`: only directory-listing views opt in, so that
 * dialogs and mutate-only callers never poll.
 *
 * `prefixOverride` also mirrors `useFiles`: callers in a route tree `useCurrentPath` can't
 * resolve (e.g. nextgen, under `/nova/...`) pass the S3 prefix explicitly.
 */
export const useFilesInfinite = (
  retryWhenNotReady = false,
  prefixOverride?: string
): UseFilesInfiniteResult => {
  const user = useUser();
  const navigate = useNavigate();
  const { projectId, datasetId } = useParams();
  const directoryPath = useCurrentPath();
  const [useParentKey] = useQueryParam("preview", BooleanParam);
  const objectKey = resolveObjectKey(directoryPath, user?.email, projectId, datasetId);
  const parentKey = useParentKey ? objectKey.replace(/\/[^/]+$/, "") : null;
  const prefix = prefixOverride ?? parentKey ?? objectKey;

  const virtualDirResp = useVirtualDirectory(prefix, false);

  // See the equivalent refs in `useFiles`: bounded auto-refresh of a directory that lists
  // as not-yet-materialized, reset whenever the directory (SWR key) changes.
  const retryAttempts = useRef(0);
  const retriedPrefix = useRef(prefix);
  if (retriedPrefix.current !== prefix) {
    retriedPrefix.current = prefix;
    retryAttempts.current = 0;
  }

  const [extraChildren, setExtraChildren] = useState<FileInfo[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Reset accumulated pages whenever the user navigates to a different directory.
  useEffect(() => {
    setExtraChildren([]);
    setNextPageToken(null);
    setHasMore(false);
  }, [prefix]);

  const firstPageUrl = !virtualDirResp
    ? `${baseUrl}/${encodeObjectKey(prefix)}?limit=${PAGE_LIMIT}`
    : null;

  const filesResp = useFetcher<FilesListResponse>(firstPageUrl, {
    // The function form is intentional and must not be memoized; see `useFiles`.
    ...(retryWhenNotReady
      ? {
          refreshInterval: (data: FilesListResponse | undefined) =>
            getFileListingRetryInterval(data, retryAttempts.current),
          onSuccess: (data: FilesListResponse) => {
            retryAttempts.current = isDirectoryNotReady(data) ? retryAttempts.current + 1 : 0;
          },
        }
      : {}),
  });

  // Sync the pagination cursor from the first-page response into state so that
  // loadMore can use it even after subsequent pages have updated nextPageToken.
  useEffect(() => {
    if (filesResp.data && !filesResp.isLoading) {
      setNextPageToken(filesResp.data.next_continuation_token ?? null);
      setHasMore(filesResp.data.has_more ?? false);
    }
  }, [filesResp.data, filesResp.isLoading]);

  const handlePreviewNavigation = useCallback(
    (fileInfo: FileInfo) =>
      navigate("..", {
        relative: "path",
        state: { preview: { key: fileInfo.key, size: fileInfo.size } },
      }),
    [navigate]
  );

  useEffect(() => {
    if (useParentKey && !filesResp.isLoading) {
      const previewFile = filesResp.data?.children?.find((file) => file.key === objectKey);
      if (previewFile) {
        handlePreviewNavigation(previewFile);
      }
    }
  }, [useParentKey, objectKey, filesResp]);

  useEffect(() => {
    const pointer = filesResp.data?.current_file;
    if (!filesResp.isLoading && pointer?.type === FileType.FILE) {
      handlePreviewNavigation(pointer);
    }
  }, [objectKey, filesResp]);

  const loadMore = useCallback(async () => {
    if (!nextPageToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const qs = queryString.stringify({
        limit: PAGE_LIMIT,
        continuation_token: nextPageToken,
      });
      const data = await apiClient<FilesListResponse>(
        `${baseUrl}/${encodeObjectKey(prefix)}?${qs}`
      );
      if (data?.children) {
        setExtraChildren((prev) => [...prev, ...data.children!]);
      }
      setNextPageToken(data?.next_continuation_token ?? null);
      setHasMore(data?.has_more ?? false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextPageToken, isLoadingMore, prefix]);

  // SWR refetches the first page (on revalidation, or via the not-ready poll) while later
  // pages stay in state, so the same object can arrive twice. S3 lists keys in a stable
  // order, so keeping the first occurrence preserves that order without duplicating rows.
  const allChildren = useMemo(() => {
    const seen = new Set<string>();
    return [...(filesResp.data?.children ?? []), ...extraChildren].filter((child) => {
      if (seen.has(child.key)) {
        return false;
      }
      seen.add(child.key);
      return true;
    });
  }, [filesResp.data?.children, extraChildren]);

  if (virtualDirResp) {
    return {
      data: virtualDirResp.data,
      allChildren: virtualDirResp.data?.children ?? [],
      isLoading: virtualDirResp.isLoading,
      isLoadingMore: false,
      error: undefined,
      hasMore: false,
      loadMore: async () => {},
    };
  }

  return {
    data: filesResp.data,
    allChildren,
    isLoading: filesResp.isLoading,
    isLoadingMore,
    error: filesResp.error,
    hasMore,
    loadMore,
  };
};
